import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, FolderKanban, Users } from 'lucide-react';

export const Campaigns = () => {
  const { get, post, put, del } = useApi();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    beschreibung: '',
    aktiv: true
  });

  const loadCampaigns = useCallback(async () => {
    try {
      const data = await get('/campaigns');
      setCampaigns(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Kampagnen');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const resetForm = () => {
    setFormData({
      name: '',
      beschreibung: '',
      aktiv: true
    });
    setEditingCampaign(null);
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      beschreibung: campaign.beschreibung || '',
      aktiv: campaign.aktiv
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCampaign) {
        await put(`/campaigns/${editingCampaign.id}`, formData);
        toast.success('Kampagne aktualisiert');
      } else {
        await post('/campaigns', formData);
        toast.success('Kampagne erstellt');
      }
      setDialogOpen(false);
      resetForm();
      loadCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async () => {
    try {
      await del(`/campaigns/${selectedCampaign.id}`);
      toast.success('Kampagne gelöscht');
      setDeleteDialogOpen(false);
      loadCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="campaigns-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Kampagnen</h1>
          <p className="text-gray-500 mt-1">
            Verwalten Sie Ihre Vertriebskampagnen
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#E63946] hover:bg-[#D62839]" data-testid="create-campaign-button">
              <Plus className="h-4 w-4 mr-2" />
              Neue Kampagne
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCampaign ? 'Kampagne bearbeiten' : 'Neue Kampagne'}</DialogTitle>
              <DialogDescription>
                {editingCampaign ? 'Aktualisieren Sie die Kampagnendaten' : 'Erstellen Sie eine neue Vertriebskampagne'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="campaign-form">
              <div className="space-y-2">
                <Label htmlFor="name">Kampagnenname *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Q1 2026 Akquise"
                  required
                  data-testid="campaign-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="beschreibung">Beschreibung</Label>
                <textarea
                  id="beschreibung"
                  className="w-full min-h-[80px] p-3 border border-gray-200 rounded-md focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none"
                  value={formData.beschreibung}
                  onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
                  placeholder="Beschreibung der Kampagne..."
                  data-testid="campaign-description-input"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="aktiv">Kampagne aktiv</Label>
                <Switch
                  id="aktiv"
                  checked={formData.aktiv}
                  onCheckedChange={(checked) => setFormData({ ...formData, aktiv: checked })}
                  data-testid="campaign-active-switch"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" className="bg-[#E63946] hover:bg-[#D62839]" data-testid="campaign-submit-button">
                  {editingCampaign ? 'Aktualisieren' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {campaigns.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FolderKanban className="h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Keine Kampagnen vorhanden</h3>
            <p className="text-gray-500 text-center mb-4">
              Erstellen Sie Ihre erste Kampagne, um Leads zu organisieren
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((campaign) => (
            <Card key={campaign.id} className={`hover:shadow-md transition-shadow ${!campaign.aktiv ? 'opacity-60' : ''}`} data-testid={`campaign-card-${campaign.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <FolderKanban className="h-5 w-5 text-[#E63946]" />
                    <CardTitle className="text-lg">{campaign.name}</CardTitle>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(campaign)}
                      data-testid={`edit-campaign-${campaign.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDeleteDialog(campaign)}
                      data-testid={`delete-campaign-${campaign.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {campaign.beschreibung && (
                    <p className="text-sm text-gray-600 line-clamp-2">{campaign.beschreibung}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{campaign.lead_count} Leads</span>
                    </div>
                    <Badge className={campaign.aktiv ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {campaign.aktiv ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </div>
                  <div className="text-xs text-gray-400 pt-2 border-t">
                    Erstellt: {formatDate(campaign.created_at)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kampagne löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie die Kampagne "{selectedCampaign?.name}" wirklich löschen?
              {selectedCampaign?.lead_count > 0 && (
                <span className="block mt-2 text-red-600 font-medium">
                  Achtung: Diese Kampagne hat noch {selectedCampaign.lead_count} zugeordnete Leads.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-campaign"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
